import React, { useEffect, useState } from 'react';
import { Alert, Button, Divider, Form, Input, Space, Typography } from 'antd';

const { Text } = Typography;

const isPlaceholderEmail = (email) =>
  typeof email === 'string' && /unassigned\+staff\d+@amanhmo\.local/i.test(email.trim());

/**
 * Editable staff contact fields (email / full name / phone).
 * Used for self-profile and admin staff / facility-user management.
 *
 * passwordMode:
 * - 'self'  current + new + confirm (required together if any password field is filled)
 * - 'admin' new + confirm (optional; sets the account password)
 */
const StaffProfileEditor = ({
  initialValues = {},
  onSubmit,
  onCancel,
  submitting = false,
  showStaffId = true,
  submitLabel = 'Save changes',
  passwordMode = null,
  // Internal staff sign in with email, so it is required for them. Facility
  // staff sign in with a generated login code — for them email is optional.
  emailRequired = true,
}) => {
  const [form] = Form.useForm();
  const [placeholderHint, setPlaceholderHint] = useState(false);

  useEffect(() => {
    form.setFieldsValue({
      full_name: initialValues.full_name || initialValues.fullName || initialValues.name || '',
      email: initialValues.email || '',
      phone: initialValues.phone || '',
      current_password: '',
      password: '',
      confirm_password: '',
    });
    setPlaceholderHint(isPlaceholderEmail(initialValues.email));
  }, [form, initialValues]);

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onSubmit}
      onValuesChange={(_, all) => setPlaceholderHint(isPlaceholderEmail(all.email))}
    >
      {showStaffId && (initialValues.staffId || initialValues.staff_id) ? (
        <Form.Item label="Staff ID">
          <Text>{initialValues.staffId || initialValues.staff_id}</Text>
        </Form.Item>
      ) : null}

      {placeholderHint ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Placeholder email detected"
          description="Replace this with the staff member’s real email. Login and password reset use email as the primary identity."
        />
      ) : null}

      <Form.Item
        label="Full name"
        name="full_name"
      >
        <Input placeholder="Jane Doe" allowClear />
      </Form.Item>

      <Form.Item
        label="Email"
        name="email"
        rules={[
          ...(emailRequired ? [{ required: true, message: 'Email is required' }] : []),
          { type: 'email', message: 'Enter a valid email address' },
        ]}
        extra={emailRequired
          ? 'Used for login and password reset. Must be unique.'
          : 'Optional. Facility staff sign in with their login code, not email. Leave blank — never reuse the facility admin email.'}
      >
        <Input placeholder={emailRequired ? 'name@example.com' : 'Optional'} autoComplete="email" allowClear={!emailRequired} />
      </Form.Item>

      <Form.Item label="Phone" name="phone">
        <Input placeholder="+234 …" allowClear />
      </Form.Item>

      {passwordMode ? (
        <>
          <Divider orientation="left" plain style={{ marginTop: 8 }}>
            {passwordMode === 'self' ? 'Change password' : 'Set password'}
          </Divider>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
            {passwordMode === 'self'
              ? 'Leave blank to keep your current password.'
              : 'Leave blank to keep the current password. The new password is never shown again.'}
          </Text>

          {passwordMode === 'self' ? (
            <Form.Item
              label="Current password"
              name="current_password"
              dependencies={['password', 'confirm_password']}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const anyPassword = Boolean(
                      String(getFieldValue('password') || '').trim()
                      || String(getFieldValue('confirm_password') || '').trim()
                      || String(value || '').trim()
                    );
                    if (!anyPassword) return Promise.resolve();
                    if (!String(value || '').trim()) {
                      return Promise.reject(new Error('Enter your current password'));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Input.Password autoComplete="current-password" />
            </Form.Item>
          ) : null}

          <Form.Item
            label="New password"
            name="password"
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const anyPassword = Boolean(
                    String(getFieldValue('current_password') || '').trim()
                    || String(getFieldValue('confirm_password') || '').trim()
                    || String(value || '').trim()
                  );
                  if (!anyPassword) return Promise.resolve();
                  if (String(value || '').length < 8) {
                    return Promise.reject(new Error('Password must be at least 8 characters'));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" placeholder="At least 8 characters" />
          </Form.Item>

          <Form.Item
            label="Confirm new password"
            name="confirm_password"
            dependencies={['password']}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const next = getFieldValue('password');
                  if (!next && !value) return Promise.resolve();
                  if (next !== value) {
                    return Promise.reject(new Error('Passwords do not match'));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </>
      ) : null}

      <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
        {onCancel ? <Button onClick={onCancel}>Cancel</Button> : null}
        <Button type="primary" htmlType="submit" loading={submitting}>
          {submitLabel}
        </Button>
      </Space>
    </Form>
  );
};

export default StaffProfileEditor;
export { isPlaceholderEmail };
